import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown"]
  static values = {
    url: { type: String, default: "/tags/autocomplete" },
    folderUrl: { type: String, default: "/folders/autocomplete" },
    debounce: { type: Number, default: 200 }
  }

  connect() {
    this.selectedIndex = -1
    this.abortController = null
    this.currentQueryType = null
    this.currentNegated = false

    const input = this.inputTarget
    if (input.value.length > 0) {
      input.setSelectionRange(input.value.length, input.value.length)
    }
  }

  disconnect() {
    this.cancelFetch()
    this.clearDebounce()
  }

  onInput() {
    this.clearDebounce()

    const match = this.extractCurrentQuery()

    if (!match) {
      this.hideDropdown()
      return
    }

    this.currentQueryType = match.type
    this.currentNegated = match.negated || false

    this.debounceTimer = setTimeout(() => {
      this.fetchSuggestions(match)
    }, this.debounceValue)
  }

  onKeydown(event) {
    if (!this.hasDropdownTarget || this.dropdownTarget.hidden) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.moveSelection(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.moveSelection(-1)
        break
      case "Enter":
        if (this.selectedIndex >= 0) {
          event.preventDefault()
          this.selectCurrentItem()
        }
        break
      case "Escape":
        this.hideDropdown()
        break
      case "Tab":
        if (this.selectedIndex >= 0) {
          event.preventDefault()
          this.selectCurrentItem()
        } else {
          this.hideDropdown()
        }
        break
    }
  }

  onBlur() {
    // Delay hiding to allow click on dropdown items
    setTimeout(() => this.hideDropdown(), 150)
  }

  extractCurrentQuery() {
    const input = this.inputTarget
    const value = input.value
    const cursorPos = input.selectionStart

    // Find the start of the current word (looking backwards from cursor)
    let wordStart = cursorPos
    while (wordStart > 0 && !/\s/.test(value[wordStart - 1])) {
      wordStart--
    }

    const currentWord = value.slice(wordStart, cursorPos)

    // Check for negated patterns first
    if (currentWord.toLowerCase().startsWith("-tag:") && currentWord.length > 5) {
      return { type: "tag", query: currentWord.slice(5), negated: true }
    }

    if (currentWord.toLowerCase().startsWith("-in:") && currentWord.length > 4) {
      return { type: "folder", query: currentWord.slice(4), negated: true }
    }

    // Check for tag: pattern
    if (currentWord.toLowerCase().startsWith("tag:") && currentWord.length > 4) {
      return { type: "tag", query: currentWord.slice(4) }
    }

    // Check for in:folder pattern
    if (currentWord.toLowerCase().startsWith("in:") && currentWord.length > 3) {
      return { type: "folder", query: currentWord.slice(3) }
    }

    return null
  }

  async fetchSuggestions(match) {
    this.cancelFetch()
    this.abortController = new AbortController()

    const fetchUrl = match.type === "tag" ? this.urlValue : this.folderUrlValue

    try {
      const url = new URL(fetchUrl, window.location.origin)
      url.searchParams.set("query", match.query)

      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: {
          "Accept": "application/json"
        }
      })

      if (!response.ok) return

      const suggestions = await response.json()
      this.showSuggestions(suggestions)
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Autocomplete error:", error)
      }
    }
  }

  showSuggestions(suggestions) {
    if (suggestions.length === 0) {
      this.hideDropdown()
      return
    }

    const neg = this.currentNegated ? "-" : ""
    const prefix = this.currentQueryType === "tag" ? `${neg}tag:` : `${neg}in:`

    this.selectedIndex = -1
    // Nodes, not an HTML string: a title is attacker-ish input (a shared
    // folder's owner picks it), and interpolating it into data-title lets a
    // quote break out of the attribute (#3114).
    this.dropdownTarget.replaceChildren(...suggestions.map((s, index) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "tag-autocomplete__item"
      button.dataset.index = index
      button.dataset.title = s.title
      button.dataset.action = "click->tag-autocomplete#selectItem"
      button.textContent = `${prefix}${s.title}`
      return button
    }))

    this.dropdownTarget.hidden = false
  }

  hideDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.hidden = true
      this.dropdownTarget.innerHTML = ""
    }
    this.selectedIndex = -1
    this.currentQueryType = null
    this.currentNegated = false
  }

  moveSelection(direction) {
    const items = this.dropdownTarget.querySelectorAll(".tag-autocomplete__item")
    if (items.length === 0) return

    // Remove current selection
    if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
      items[this.selectedIndex].classList.remove("tag-autocomplete__item--selected")
    }

    // Update index
    this.selectedIndex += direction
    if (this.selectedIndex < 0) this.selectedIndex = items.length - 1
    if (this.selectedIndex >= items.length) this.selectedIndex = 0

    // Add new selection
    items[this.selectedIndex].classList.add("tag-autocomplete__item--selected")
    items[this.selectedIndex].scrollIntoView({ block: "nearest" })
  }

  selectItem(event) {
    const title = event.currentTarget.dataset.title
    this.insertSuggestion(title)
  }

  selectCurrentItem() {
    const items = this.dropdownTarget.querySelectorAll(".tag-autocomplete__item")
    if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
      const title = items[this.selectedIndex].dataset.title
      this.insertSuggestion(title)
    }
  }

  insertSuggestion(title) {
    const input = this.inputTarget
    const value = input.value
    const cursorPos = input.selectionStart

    // Find the start of the current word
    let wordStart = cursorPos
    while (wordStart > 0 && !/\s/.test(value[wordStart - 1])) {
      wordStart--
    }

    // Build the replacement text
    const neg = this.currentNegated ? "-" : ""
    let replacement
    if (this.currentQueryType === "tag") {
      replacement = `${neg}tag:${title}`
    } else {
      replacement = title.includes(" ") ? `${neg}in:"${title}"` : `${neg}in:${title}`
    }

    const before = value.slice(0, wordStart)
    const after = value.slice(cursorPos)
    const newValue = before + replacement + (after.startsWith(" ") ? "" : " ") + after

    input.value = newValue.replace(/\s+/g, " ")

    // Position cursor after the inserted suggestion
    const newCursorPos = wordStart + replacement.length + 1
    input.setSelectionRange(newCursorPos, newCursorPos)
    input.focus()

    this.hideDropdown()
  }

  cancelFetch() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  clearDebounce() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }
}
